// ============================================================================
// BOQ — Classification taxonomies hook
// ============================================================================
// Reads boq.classification_taxonomies + boq.classification_nodes. The boq
// schema must be in the Supabase project's exposed schemas list for these
// queries to reach PostgREST (Dashboard → Project Settings → API).
//
// Returns each taxonomy with its nodes pre-sorted; nodes carry a `children`
// array when the taxonomy nests.
//
// Writes are limited to two things, both curator-only in the database
// (boq.can_curate_library): authoring the `trade_type` taxonomy, and setting
// `short_code` — the fragment a node contributes to a library item code.
// `canCurate` mirrors that grant so the UI disables what the DB would refuse.
// Nothing here changes an item code; short_code is recorded and unused today.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';
import { buildSubtradeLinks } from '../utils/tradeGroupSubtrades';

export const BRIO_TRADE_TAXONOMY = 'brio_trade';
export const TRADE_TYPE_TAXONOMY = 'trade_type';
export const TRADE_GROUP_TAXONOMY = 'trade_group';

const NODE_COLUMNS =
  'id, taxonomy_id, code, label, alt_label_in, parent_id, sort_order, is_active, short_code, '
  + 'maps_to_node_id, series_start, series_end, series_width';

const mapTaxonomy = (t) => ({
  id: t.id,
  code: t.code,
  name: t.name,
  description: t.description,
  sortOrder: t.sort_order,
});

const mapNode = (n) => ({
  id: n.id,
  taxonomyId: n.taxonomy_id,
  code: n.code,
  label: n.label,
  altLabelIn: n.alt_label_in,
  parentId: n.parent_id,
  sortOrder: n.sort_order,
  isActive: n.is_active,
  shortCode: n.short_code || '',
  // Cross-taxonomy rollup link. On a trade_group it points at the brio_trade
  // chapter that group rolls up to; on a trade_type, at the brio_trade
  // sub-trade the type sits under.
  mapsToNodeId: n.maps_to_node_id || null,
  // A band of sequence numbers. On a trade type, the numbers its items draw
  // from; on a canonical sub-trade, the range split across the types under it.
  // Null elsewhere, and until a curator sets one.
  seriesStart: n.series_start,
  seriesEnd: n.series_end,
  // How many numbers a trade type asks for when its sub-trade's range is
  // split. Null means "share the balance". An intent, kept apart from the band
  // currently held — reading it off the band would make every type look as
  // though it had asked for what it has, and the equal share would never apply.
  seriesWidth: n.series_width,
});

const buildTree = (nodes) => {
  const byId = new Map();
  nodes.forEach((n) => byId.set(n.id, { ...n, children: [] }));
  const roots = [];
  byId.forEach((node) => {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (arr) => {
    arr.sort((a, b) => a.sortOrder - b.sortOrder);
    arr.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

// Slug for a new node's `code`, which is the node identifier and separate from
// short_code. Prefixed with the anchor's slug so a trade type reads as
// `civil.earthwork.blasting` and stays unique within its taxonomy.
const slugify = (label, anchorCode) => {
  const base = (label || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'node';
  return anchorCode ? `${anchorCode}.${base}` : base;
};

const requireSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data?.session) return 'Please sign in to edit taxonomies.';
  return null;
};

export const useBoqTaxonomies = () => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canCurate, setCanCurate] = useState(false);
  // Which canonical sub-trades each trade group is ticked for (Map trade group
  // id → Set of sub-trade ids). Read from boq.subtrade_trade_groups; New item
  // and the Edit modal narrow their sub-trade dropdown by it.
  const [subtradesByGroup, setSubtradesByGroup] = useState(() => new Map());

  // `quiet` re-reads without raising `loading`. The page renders a loading
  // branch instead of its sections while that is true, which unmounts them —
  // and the column locks and the open sections are component state, so a
  // refresh after a save would snap the lock shut and collapse the page under
  // whoever just typed. Only the first load, which has nothing to preserve,
  // shows it.
  const fetchAll = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [{ data: taxRows, error: taxErr }, { data: nodeRows, error: nodeErr }, grant, links] = await Promise.all([
        supabase.schema('boq').from('classification_taxonomies')
          .select('id, code, name, description, sort_order')
          .order('sort_order'),
        supabase.schema('boq').from('classification_nodes')
          .select(NODE_COLUMNS)
          .order('sort_order'),
        supabase.schema('boq').rpc('can_curate_library'),
        supabase.schema('boq').from('subtrade_trade_groups').select('subtrade_node_id, trade_group_node_id'),
      ]);
      if (taxErr) throw taxErr;
      if (nodeErr) throw nodeErr;
      if (links.error) throw links.error;
      setSubtradesByGroup(buildSubtradeLinks(links.data));

      const taxs = (taxRows || []).map(mapTaxonomy);
      const allNodes = (nodeRows || []).map(mapNode);

      setTaxonomies(taxs.map((t) => {
        const nodes = allNodes.filter((n) => n.taxonomyId === t.id);
        return { ...t, nodes, tree: buildTree(nodes) };
      }));
      // A failed grant check reads as "no": hiding the editors is the safe miss.
      setCanCurate(grant.error ? false : grant.data === true);
    } catch (err) {
      console.error('[useBoqTaxonomies] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'taxonomies' }));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Add a node to a taxonomy. `mapsToNodeId` is the brio_trade sub-trade the
  // new trade type is filed under, and supplies the code's prefix.
  const createNode = useCallback(async ({ taxonomyId, label, mapsToNodeId = null }) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const trimmed = (label || '').trim();
    if (!trimmed) return { success: false, error: 'A name is required.' };
    try {
      const tax = taxonomies.find((t) => t.id === taxonomyId);
      const anchor = mapsToNodeId
        ? (taxonomies.flatMap((t) => t.nodes).find((n) => n.id === mapsToNodeId) || null)
        : null;
      let code = slugify(trimmed, anchor?.code);
      // Codes are unique per taxonomy; disambiguate rather than fail the insert.
      const taken = new Set((tax?.nodes || []).map((n) => n.code));
      if (taken.has(code)) {
        let i = 2;
        while (taken.has(`${code}-${i}`)) i += 1;
        code = `${code}-${i}`;
      }
      const payload = {
        taxonomy_id: taxonomyId,
        parent_id: null,
        code,
        label: trimmed,
        maps_to_node_id: mapsToNodeId,
        sort_order: (tax?.nodes || []).reduce((max, n) => Math.max(max, n.sortOrder ?? 0), 0) + 1,
      };
      const { data, error: insErr } = await supabase
        .schema('boq').from('classification_nodes').insert(payload).select(NODE_COLUMNS).single();
      if (insErr) throw insErr;
      await fetchAll({ quiet: true });
      return { success: true, node: mapNode(data) };
    } catch (err) {
      console.error('[useBoqTaxonomies] createNode failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'trade type' }) };
    }
  }, [taxonomies, fetchAll]);

  const renameNode = useCallback(async (nodeId, label) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const trimmed = (label || '').trim();
    if (!trimmed) return { success: false, error: 'A name is required.' };
    try {
      const { data, error: updErr } = await supabase
        .schema('boq').from('classification_nodes').update({ label: trimmed }).eq('id', nodeId)
        .select('id');
      if (updErr) throw updErr;
      // An UPDATE that RLS filters out matches zero rows rather than raising.
      // On trade_type that is anyone who is not a library curator.
      if (!data || data.length === 0) {
        return { success: false, error: 'You do not have permission to rename this.' };
      }
      setTaxonomies((prev) => prev.map((t) => {
        const nodes = t.nodes.map((n) => (n.id === nodeId ? { ...n, label: trimmed } : n));
        return { ...t, nodes, tree: buildTree(nodes) };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] renameNode failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'taxonomy node' }) };
    }
  }, []);

  // The code-name fragment: 2 letters on a trade group, 2-3 on a canonical
  // sub-trade. The DB CHECK enforces the shape, a trigger the curator grant.
  const setShortCode = useCallback(async (nodeId, shortCode) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const raw = (shortCode || '').trim().toUpperCase();
    if (raw && !/^[A-Z]{2,3}$/.test(raw)) {
      return { success: false, error: 'A code must be 2 or 3 letters, e.g. EW or FLR.' };
    }
    try {
      const { error: updErr } = await supabase
        .schema('boq').from('classification_nodes').update({ short_code: raw || null }).eq('id', nodeId);
      if (updErr) throw updErr;
      setTaxonomies((prev) => prev.map((t) => {
        const nodes = t.nodes.map((n) => (n.id === nodeId ? { ...n, shortCode: raw } : n));
        return { ...t, nodes, tree: buildTree(nodes) };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] setShortCode failed', err);
      // The guard trigger raises a sentence meant for a human — the duplicate
      // it refused, or the grant it lacks. Surface it rather than letting
      // formatSupabaseError reduce it to a generic message.
      if (err?.code === '23505' || err?.code === '42501') {
        return { success: false, error: err.message };
      }
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'code name' }) };
    }
  }, []);

  // Refused while the node is still tagged on any item, so a delete can never
  // orphan a classification. RLS would also refuse a non-curator, but silently
  // — it filters the row out rather than raising — so the count check runs
  // first and the delete reports its own row count.
  const deleteNode = useCallback(async (nodeId) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    try {
      const { count, error: useErr } = await supabase
        .schema('boq').from('item_version_classifications')
        .select('item_version_id', { count: 'exact', head: true })
        .eq('classification_node_id', nodeId);
      if (useErr) throw useErr;
      if (count) {
        return { success: false, error: `In use by ${count} item version${count === 1 ? '' : 's'}. Clear it from those items first.` };
      }

      const { data, error: delErr } = await supabase
        .schema('boq').from('classification_nodes').delete().eq('id', nodeId).select('id');
      if (delErr) throw delErr;
      // A DELETE that RLS filters out matches zero rows instead of raising.
      if (!data || data.length === 0) {
        return { success: false, error: 'Only library curators may remove a trade type.' };
      }
      await fetchAll({ quiet: true });
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] deleteNode failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'trade type' }) };
    }
  }, [fetchAll]);

  // The sequence band a trade type's items draw from. Both ends or neither;
  // the DB CHECK enforces the shape, the guard trigger the curator grant and
  // that two series under one sub-trade never overlap.
  const setSeries = useCallback(async (nodeId, start, end) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const blank = (v) => v === '' || v === null || v === undefined;
    const clearing = blank(start) && blank(end);
    const from = clearing ? null : Number(start);
    const to = clearing ? null : Number(end);
    if (!clearing) {
      if (!Number.isInteger(from) || !Number.isInteger(to)) {
        return { success: false, error: 'A series needs a whole number at each end.' };
      }
      if (from < 0 || to > 9999) {
        return { success: false, error: 'A series must sit between 0 and 9999 — the item code carries 4 digits.' };
      }
      if (to < from) return { success: false, error: 'The end of a series cannot be below its start.' };
    }
    try {
      const { error: updErr } = await supabase
        .schema('boq').from('classification_nodes')
        .update({ series_start: from, series_end: to }).eq('id', nodeId);
      if (updErr) throw updErr;
      setTaxonomies((prev) => prev.map((t) => {
        const nodes = t.nodes.map((n) => (n.id === nodeId ? { ...n, seriesStart: from, seriesEnd: to } : n));
        return { ...t, nodes, tree: buildTree(nodes) };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] setSeries failed', err);
      if (err?.code === '23505' || err?.code === '42501' || err?.code === '23514') {
        return { success: false, error: err.message };
      }
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'series' }) };
    }
  }, []);

  // How many numbers a trade type asks for in the next split. Stored, not
  // applied: the split runs when a curator sets the sub-trade's range or
  // presses the refresh beside it, never on its own.
  const setSeriesWidth = useCallback(async (nodeId, width) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const blank = width === '' || width === null || width === undefined;
    const value = blank ? null : Number(width);
    if (!blank) {
      if (!Number.isInteger(value) || value < 1) {
        return { success: false, error: 'A count must be a whole number of 1 or more. Leave it empty to share the balance.' };
      }
      if (value > 10000) {
        return { success: false, error: 'A count cannot exceed 10000 — the item code carries 4 digits.' };
      }
    }
    try {
      const { data, error: updErr } = await supabase
        .schema('boq').from('classification_nodes')
        .update({ series_width: value }).eq('id', nodeId).select('id');
      if (updErr) throw updErr;
      // An UPDATE that RLS filters out matches zero rows instead of raising.
      if (!data || data.length === 0) {
        return { success: false, error: 'Only library curators may set how many numbers a trade type takes.' };
      }
      setTaxonomies((prev) => prev.map((t) => {
        const nodes = t.nodes.map((n) => (n.id === nodeId ? { ...n, seriesWidth: value } : n));
        return { ...t, nodes, tree: buildTree(nodes) };
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] setSeriesWidth failed', err);
      if (err?.code === '42501' || err?.code === '23514') {
        return { success: false, error: err.message };
      }
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'count' }) };
    }
  }, []);

  // A canonical sub-trade's own range, and the split of it across the trade
  // types under that sub-trade. One RPC so the whole thing lands or none of it
  // does: the bands are cleared and rewritten, and a half-applied split would
  // leave two types holding the same numbers.
  //
  // The caller supplies the bands (distributeSeries in
  // src/boq/utils/seriesDistribution.js owns that rule, so the confirm dialog
  // can show exactly what is about to be written). The database still checks
  // them: curator-only, no overlap, and inside the sub-trade's range.
  // { refresh: false } skips the re-read, so a cascade across many sub-trades
  // reads once at the end instead of once per row.
  const setSubtradeSeries = useCallback(async (subtradeId, start, end, bands = [], { refresh = true } = {}) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const blank = (v) => v === '' || v === null || v === undefined;
    const clearing = blank(start) && blank(end);
    const from = clearing ? null : Number(start);
    const to = clearing ? null : Number(end);
    if (!clearing) {
      if (!Number.isInteger(from) || !Number.isInteger(to)) {
        return { success: false, error: 'A range needs a whole number at each end.' };
      }
      if (from < 0 || to > 9999) {
        return { success: false, error: 'A range must sit between 0 and 9999 — the item code carries 4 digits.' };
      }
      if (to < from) return { success: false, error: 'The end of a range cannot be below its start.' };
    }
    try {
      const { error: rpcErr } = await supabase.schema('boq').rpc('set_subtrade_series', {
        p_subtrade_id: subtradeId,
        p_start: from,
        p_end: to,
        p_bands: clearing ? [] : bands.map((b) => ({
          id: b.id, seriesStart: b.seriesStart, seriesEnd: b.seriesEnd,
        })),
      });
      if (rpcErr) throw rpcErr;
      // The RPC clears every band under the sub-trade and rewrites them across
      // two taxonomies, so re-read rather than modelling that by hand. Quietly:
      // a loading flash here would take the column's lock and the open sections
      // with it.
      if (refresh) await fetchAll({ quiet: true });
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] setSubtradeSeries failed', err);
      if (err?.code === '23505' || err?.code === '42501' || err?.code === '23514'
          || err?.code === 'P0002' || err?.message) {
        return { success: false, error: err.message };
      }
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'range' }) };
    }
  }, [fetchAll]);

  // Rewrite sort_order for a set of sibling nodes to match the order given.
  // Ten-step increments, the same convention the subtrade template editor uses,
  // so a later insert has room to slot between two rows without renumbering.
  const reorderNodes = useCallback(async (orderedIds) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    if (!orderedIds || orderedIds.length < 2) return { success: true };

    // Optimistic: the list re-sorts on sortOrder, so this lands before the
    // round-trip and the row does not snap back under the cursor.
    const nextOrder = new Map(orderedIds.map((id, i) => [id, (i + 1) * 10]));
    setTaxonomies((prev) => prev.map((t) => {
      if (!t.nodes.some((n) => nextOrder.has(n.id))) return t;
      const nodes = t.nodes.map((n) => (nextOrder.has(n.id) ? { ...n, sortOrder: nextOrder.get(n.id) } : n));
      return { ...t, nodes, tree: buildTree(nodes) };
    }));

    try {
      const results = await Promise.all(orderedIds.map((id, i) => (
        supabase.schema('boq').from('classification_nodes')
          .update({ sort_order: (i + 1) * 10 }).eq('id', id).select('id')
      )));
      const failed = results.find((r) => r.error);
      if (failed) throw failed.error;
      // An UPDATE that RLS filters out matches zero rows instead of raising,
      // so a non-curator would otherwise see the order stick and then revert
      // on the next load with no explanation.
      if (results.some((r) => !r.data || r.data.length === 0)) {
        await fetchAll({ quiet: true });
        return { success: false, error: 'Only library curators may reorder trade types.' };
      }
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] reorderNodes failed', err);
      await fetchAll({ quiet: true });
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'order' }) };
    }
  }, [fetchAll]);

  // Tick or untick a trade group on a canonical sub-trade. Optimistic, then
  // checked: an insert or delete that RLS filters out matches zero rows rather
  // than raising, so an empty result is a refusal and the tick is put back.
  // Ticks or unticks sub-trades for one trade group. Several ids at once is a
  // chapter tick: only the ones that actually change are written, in one
  // statement, so a chapter half ticked tops up rather than inserting a
  // duplicate the UNIQUE pair would refuse.
  const setTradeGroupSubtrades = useCallback(async (tradeGroupId, subtradeIds, on) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    const tg = String(tradeGroupId);
    const current = subtradesByGroup.get(tg) || new Set();
    const ids = (subtradeIds || []).map(String).filter((id) => current.has(id) !== on);
    if (ids.length === 0) return { success: true };
    const apply = (want) => setSubtradesByGroup((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(tg) || []);
      ids.forEach((id) => { if (want) set.add(id); else set.delete(id); });
      next.set(tg, set);
      return next;
    });
    apply(on);
    try {
      const q = supabase.schema('boq').from('subtrade_trade_groups');
      const { data, error: wErr } = on
        ? await q.insert(ids.map((id) => ({ subtrade_node_id: id, trade_group_node_id: tradeGroupId }))).select('id')
        : await q.delete().eq('trade_group_node_id', tradeGroupId).in('subtrade_node_id', ids).select('id');
      if (wErr) throw wErr;
      // A DELETE that RLS filters out matches zero rows rather than raising.
      if (!data || data.length === 0) {
        apply(!on);
        return { success: false, error: 'Only library curators may change which sub-trades a trade group offers.' };
      }
      return { success: true };
    } catch (err) {
      console.error('[useBoqTaxonomies] setTradeGroupSubtrades failed', err);
      apply(!on);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'trade group link' }) };
    }
  }, [subtradesByGroup]);

  return {
    taxonomies, loading, error, canCurate, refresh: fetchAll,
    subtradesByGroup, setTradeGroupSubtrades,
    createNode, renameNode, deleteNode, setShortCode, setSeries, setSeriesWidth, setSubtradeSeries,
    reorderNodes,
  };
};
